import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack, Chip, Typography } from '@mui/material';
import { useState } from 'react';
import { api } from '@core/api.js';
import { useNotify } from '@shared/hooks/useNotify.js';
import { apiErrorMessage } from '@shared/utils/apiErrorMessage.js';

const PRESET_SCOPES = ['*', 'leads:write', 'contacts:read', 'services:read', 'bookings:read', 'bookings:write', 'quotes:write', 'tickets:write'];

export default function ApiKeyFormDialog({ onClose }) {
  const notify = useNotify();
  const [form, setForm] = useState({ name: '', scopes: ['leads:write', 'bookings:read'], rateLimit: 60, expiresAt: '' });
  const [saving, setSaving] = useState(false);

  const toggleScope = (s) => setForm((f) => ({
    ...f,
    scopes: f.scopes.includes(s) ? f.scopes.filter((x) => x !== s) : [...f.scopes, s],
  }));

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post('/v1/api-keys', {
        ...form,
        expiresAt: form.expiresAt || null,
      });
      notify.success('Clé créée');
      onClose(data.token);
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Échec'));
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={() => onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Nouvelle clé API</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Nom"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            required
            fullWidth
          />
          <Stack spacing={1} role="group" aria-labelledby="api-key-scopes-label">
            <Typography id="api-key-scopes-label" variant="subtitle2" component="p" sx={{ m: 0 }}>
              Scopes
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {PRESET_SCOPES.map((s) => {
                const selected = form.scopes.includes(s);
                return (
                  <Chip
                    key={s}
                    label={s}
                    color={selected ? 'primary' : 'default'}
                    onClick={() => toggleScope(s)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleScope(s);
                      }
                    }}
                    clickable
                    aria-pressed={selected}
                    aria-label={`Scope ${s}${selected ? ', sélectionné' : ''}`}
                  />
                );
              })}
            </Stack>
          </Stack>
          <TextField
            type="number"
            label="Rate limit (req/min)"
            value={form.rateLimit}
            onChange={(e) => setForm((f) => ({ ...f, rateLimit: +e.target.value }))}
            fullWidth
          />
          <TextField
            type="datetime-local"
            label="Expire le (optionnel)"
            InputLabelProps={{ shrink: true }}
            value={form.expiresAt}
            onChange={(e) => setForm((f) => ({ ...f, expiresAt: e.target.value }))}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()}>Annuler</Button>
        <Button onClick={submit} variant="contained" disabled={saving || !form.name}>Créer</Button>
      </DialogActions>
    </Dialog>
  );
}
